import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider, BrandingProvider } from "@/context/ThemeContext";
import Layout from "@/components/Layout";
import Auth from "@/pages/Auth";
import Dashboard from "@/pages/Dashboard";
import Events from "@/pages/Events";
import EventDetail from "@/pages/EventDetail";
import CreateEvent from "@/pages/CreateEvent";
import ExpressEvent from "@/pages/ExpressEvent";
import ExpressOffer from "@/pages/ExpressOffer";
import ExpressTrack from "@/pages/ExpressTrack";
import CheckIn from "@/pages/CheckIn";
import Profile from "@/pages/Profile";
import Agenda from "@/pages/Agenda";
import Payments from "@/pages/Payments";
import Ranking from "@/pages/Ranking";
import StageMode from "@/pages/StageMode";
import SearchEvents from "@/pages/SearchEvents";
import Messages from "@/pages/Messages";
import Reports from "@/pages/Reports";
import OnCall from "@/pages/OnCall";
import Admin from "@/pages/Admin";
import MyRepertoire from "@/pages/MyRepertoire";
import FormatSong from "@/pages/FormatSong";
import SongView from "@/pages/SongView";
import Library from "@/pages/Library";
import ManualSong from "@/pages/ManualSong";
import SupportChat from "@/components/SupportChat";
import MyPlan from "@/pages/MyPlan";
import ContractorAdvanced from "@/pages/ContractorAdvanced";
import Invite from "@/pages/Invite";
import PublicProfile from "@/pages/PublicProfile";
import Landing from "@/pages/Landing";
import Legal from "@/pages/Legal";
import CompleteProfile from "@/pages/CompleteProfile";
import PersonaSelect from "@/pages/PersonaSelect";
import MinhaEquipe from "@/pages/MinhaEquipe";
import "@/App.css";

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#09090B]">
      <div className="h-10 w-10 rounded-full border-2 border-[#FF4D00] border-t-transparent animate-spin" />
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.user_type) return <Navigate to="/escolher-perfil" replace />;
  return <Layout>{children}</Layout>;
}

function ProtectedBare({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.user_type) return <Navigate to="/escolher-perfil" replace />;
  return children;
}

function PersonaChoose() {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.user_type) return <Navigate to="/" replace />;
  return <PersonaSelect />;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function Home() {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Landing />;
  if (!user.user_type) return <Navigate to="/escolher-perfil" replace />;
  return <Layout><Dashboard /></Layout>;
}

function Routing() {
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><Auth mode="login" /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Auth mode="register" /></PublicOnly>} />
      <Route path="/forgot-password" element={<Auth mode="forgot" />} />
      <Route path="/reset-password" element={<Auth mode="reset" />} />
      <Route path="/preview/landing" element={<Landing preview />} />
      <Route path="/preview/login" element={<Auth mode="login" preview />} />
      <Route path="/verify-email" element={<Auth mode="verify" />} />
      <Route path="/m/:uid" element={<PublicProfile />} />
      <Route path="/musico/:uid" element={<PublicProfile />} />
      <Route path="/termos" element={<Legal page="termos" />} />
      <Route path="/privacidade" element={<Legal page="privacidade" />} />
      <Route path="/cookies" element={<Legal page="cookies" />} />
      <Route path="/" element={<Home />} />
      <Route path="/escolher-perfil" element={<PersonaChoose />} />
      <Route path="/eventos" element={<Protected><Events /></Protected>} />
      <Route path="/em-cima-da-hora" element={<Protected><ExpressEvent /></Protected>} />
      <Route path="/em-cima-da-hora/oferta/:aid" element={<Protected><ExpressOffer /></Protected>} />
      <Route path="/em-cima-da-hora/servico/:aid" element={<Protected><ExpressTrack /></Protected>} />
      <Route path="/eventos/novo" element={<Protected><CreateEvent /></Protected>} />
      <Route path="/eventos/:id/editar" element={<Protected><CreateEvent /></Protected>} />
      <Route path="/eventos/:id/checkin" element={<Protected><CheckIn /></Protected>} />
      <Route path="/eventos/:id" element={<Protected><EventDetail /></Protected>} />
      <Route path="/agenda" element={<Protected><Agenda /></Protected>} />
      <Route path="/pagamentos" element={<Protected><Payments /></Protected>} />
      <Route path="/ranking" element={<Protected><Ranking /></Protected>} />
      <Route path="/buscar-eventos" element={<Protected><SearchEvents /></Protected>} />
      <Route path="/mensagens" element={<Protected><Messages /></Protected>} />
      <Route path="/mensagens/:eventId" element={<Protected><Messages /></Protected>} />
      <Route path="/relatorios" element={<Protected><Reports /></Protected>} />
      <Route path="/plantao" element={<Protected><OnCall /></Protected>} />
      <Route path="/repertorio" element={<Protected><MyRepertoire /></Protected>} />
      <Route path="/repertorio/formatar" element={<Protected><FormatSong /></Protected>} />
      <Route path="/repertorio/musica/:id" element={<Protected><SongView /></Protected>} />
      <Route path="/repertorio/biblioteca" element={<Protected><Library /></Protected>} />
      <Route path="/repertorio/nova" element={<Protected><ManualSong /></Protected>} />
      <Route path="/contratante-avancado" element={<Protected><ContractorAdvanced /></Protected>} />
      <Route path="/convidar" element={<Protected><Invite /></Protected>} />
      <Route path="/minha-equipe" element={<Protected><MinhaEquipe /></Protected>} />
      <Route path="/admin" element={<Protected><Admin /></Protected>} />
      <Route path="/perfil" element={<Protected><Profile /></Protected>} />
      <Route path="/completar-perfil" element={<ProtectedBare><CompleteProfile /></ProtectedBare>} />
      <Route path="/meu-plano" element={<Protected><MyPlan /></Protected>} />
      <Route path="/musicos/:id" element={<Protected><Profile /></Protected>} />
      <Route path="/palco/:id" element={<Protected><StageMode /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <BrandingProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routing />
            <SupportChat />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </AuthProvider>
      </BrandingProvider>
    </ThemeProvider>
  );
}
